import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { unwrap } from '../utils/api';
import Icon from '../components/Icon';

export default function OrderList() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  async function load() {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      const r = await api.get('/orders/allgetOrder', { params });
      const body = unwrap(r);
      setOrders(body?.items || []);
      setTotal(body?.total || 0);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, search, statusFilter]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setSearch(searchDraft);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchDraft]);

  async function refund(_id) {
    if (!window.confirm('Mark as refunded?')) return;
    await api.post('/orders/refund', { _id });
    load();
  }

  // Hard delete: removes the order from the database entirely. Once deleted
  // it disappears from OrderList AND from every Analytics aggregation (those
  // query the orders collection directly), so revenue/order counts drop to
  // reflect the removal. There's no undo — the prompt names the order ID
  // and total so the admin can't fat-finger the wrong row.
  async function deleteOrder(o) {
    const short = String(o._id).slice(-8).toUpperCase();
    const total = Number(o.total || 0).toFixed(2);
    if (!window.confirm(`Permanently delete order #${short} ($${total})? This can't be undone, and the order will disappear from analytics too.`)) return;
    await api.post('/orders/deleteOrder', { _id: o._id });
    load();
  }

  function customerOf(o) {
    if (o.customerId?.email) {
      const name = [o.customerId.firstName, o.customerId.lastName].filter(Boolean).join(' ');
      return name ? `${name} <${o.customerId.email}>` : o.customerId.email;
    }
    return o.customerEmail || (o.guestId ? `guest:${String(o.guestId).slice(-6)}` : '—');
  }

  function statusPill(s) {
    const colors = {
      paid: { bg: '#e8f7ee', fg: '#0a7a32' },
      free: { bg: '#eef4ff', fg: '#0a5fb4' },
      pending: { bg: '#fff7e6', fg: '#9a6700' },
      failed: { bg: '#fff0f0', fg: '#b42318' },
      refunded: { bg: '#f4f4f4', fg: '#555' },
    };
    const c = colors[s] || { bg: '#eee', fg: '#333' };
    return (
      <span style={{ background: c.bg, color: c.fg, padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600 }}>
        {s || '—'}
      </span>
    );
  }

  return (
    <>
      <h1>Orders ({total})</h1>
      <div className="card" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search email, coupon code, order ID, template name, payment ref…"
          value={searchDraft}
          onChange={e => setSearchDraft(e.target.value)}
          style={{ flex: '1 1 320px', minWidth: 240 }}
        />
        <select value={statusFilter} onChange={e => { setPage(1); setStatusFilter(e.target.value); }} style={{ minWidth: 140 }}>
          <option value="">All statuses</option>
          <option value="paid">Paid</option>
          <option value="free">Free</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
          <option value="refunded">Refunded</option>
        </select>
        {loading && <span style={{ color: '#888', fontSize: 13 }}>Loading…</span>}
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 28 }}></th>
              <th>Order ID</th>
              <th>Customer</th>
              <th>Templates</th>
              <th>Subtotal</th>
              <th>Coupon</th>
              <th>Discount</th>
              <th>Total paid</th>
              <th>Status</th>
              <th>Date</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {orders.map(o => {
              const isOpen = expandedId === o._id;
              const templates = (o.items || []).map(i => i.productName).filter(Boolean);
              const templateLabel = templates.length === 0
                ? '—'
                : templates.length === 1
                  ? templates[0]
                  : `${templates[0]} +${templates.length - 1}`;
              return (
                <React.Fragment key={o._id}>
                  <tr style={{ cursor: 'pointer' }} onClick={() => setExpandedId(isOpen ? null : o._id)}>
                    <td><Icon name={isOpen ? 'keyboard_arrow_down' : 'keyboard_arrow_right'} /></td>
                    <td>
                      <code style={{ fontSize: 12 }}>{String(o._id).slice(-8)}</code>
                    </td>
                    <td>{customerOf(o)}</td>
                    <td title={templates.join(', ')}>{templateLabel}</td>
                    <td>${Number(o.subtotal || 0).toFixed(2)}</td>
                    <td>{o.couponCode ? <code>{o.couponCode}</code> : '—'}</td>
                    <td>{o.discount ? `-$${Number(o.discount).toFixed(2)}` : '—'}</td>
                    <td><strong>${Number(o.total || 0).toFixed(2)}</strong></td>
                    <td>{statusPill(o.status)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{new Date(o.createdAt).toLocaleString()}</td>
                    <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                      {o.status !== 'refunded' && o.status === 'paid' && (
                        <button className="btn small danger" onClick={() => refund(o._id)} style={{ marginRight: 6 }}>Refund</button>
                      )}
                      <button className="btn small danger" onClick={() => deleteOrder(o)} title="Permanently delete this order">Delete</button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={11} style={{ background: '#fafbfc' }}>
                        <div style={{ padding: '8px 12px', fontSize: 13 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 }}>
                            <div><strong>Full order ID:</strong> <code>{o._id}</code></div>
                            <div><strong>Customer email:</strong> {o.customerId?.email || o.customerEmail || '—'}</div>
                            <div><strong>Guest ID:</strong> {o.guestId || '—'}</div>
                            <div><strong>Payment method:</strong> {o.paymentMethod || '—'}</div>
                            <div><strong>Payment ref:</strong> {o.paymentRef ? <code>{o.paymentRef}</code> : '—'}</div>
                            <div><strong>Stripe session:</strong> {o.stripeSessionId ? <code>{o.stripeSessionId}</code> : '—'}</div>
                            <div><strong>Download token:</strong> {o.downloadToken ? <code>{String(o.downloadToken).slice(0, 12)}…</code> : '—'}</div>
                            <div><strong>Subtotal:</strong> ${Number(o.subtotal || 0).toFixed(2)}</div>
                            <div><strong>Coupon code:</strong> {o.couponCode || '—'}</div>
                            <div><strong>Discount:</strong> ${Number(o.discount || 0).toFixed(2)}</div>
                            <div><strong>Total:</strong> ${Number(o.total || 0).toFixed(2)}</div>
                            <div><strong>Status:</strong> {o.status}</div>
                            <div><strong>Created:</strong> {new Date(o.createdAt).toLocaleString()}</div>
                            {o.updatedAt && <div><strong>Updated:</strong> {new Date(o.updatedAt).toLocaleString()}</div>}
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>Items ({o.items?.length || 0})</div>
                            <table className="table" style={{ background: '#fff' }}>
                              <thead>
                                <tr>
                                  <th>Template</th>
                                  <th>Price</th>
                                  <th>Customer’s text</th>
                                  <th>Edits</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {(o.items || []).map(it => {
                                  const texts = (it.fieldValues || [])
                                    .map(fv => fv.value)
                                    .filter(v => v && String(v).trim() && v !== '__SIMCHA_CLEARED__');
                                  return (
                                    <tr key={it._id}>
                                      <td>{it.productName || <em style={{ color: '#888' }}>deleted template</em>}</td>
                                      <td>${Number(it.price || 0).toFixed(2)}</td>
                                      <td style={{ maxWidth: 380, fontSize: 12 }}>
                                        {texts.length ? texts.join('  ·  ') : <em style={{ color: '#aaa' }}>—</em>}
                                      </td>
                                      <td>{(it.editCount || 0)} / {(it.maxEdits || 5)}</td>
                                      <td>
                                        <Link className="btn small" to={`/pages/orderItem/${o._id}/${it._id}`}>
                                          View / Edit template
                                        </Link>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {orders.length === 0 && (
              <tr><td colSpan={11} style={{ textAlign: 'center', padding: 24, color: '#888' }}>
                {search ? 'No orders match your search.' : 'No orders yet.'}
              </td></tr>
            )}
          </tbody>
        </table>
        <div className="row" style={{ marginTop: 10 }}>
          <button className="btn small secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
          <span>Page {page} of {Math.max(1, Math.ceil(total / 50))}</span>
          <button className="btn small secondary" disabled={page * 50 >= total} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      </div>
    </>
  );
}
